/*
"Formants" are the primary frequency components of vowels that can identify them.
The IPA vowel symbols don't represent an exact sound, since vowels vary by language,
accent, speaker, and surrounding phonemes.
https://en.wikipedia.org/wiki/Formant
*/

import { IpaType } from "./ipa"
import { PhonemeItem, segmentPhonemes } from "./segmentPhonemes";

/** the primary frequencies of a sound */
export interface FormantInfo {
  /** range (200, 950) */
  readonly f1: number;
  /** range (700, 2450) */
  readonly f2: number;
}

export const formantRange = { f1Min: 200, f1Max: 950, f2Min: 700, f2Max: 2450 };

/** get the formant values for phoneme or list of phonemes */
export function getFormants(
  ipa: IpaType,
  formants?: Record<IpaType, number[]>,
  diphthongs?: Record<IpaType, number[]>,
): FormantInfo[] {
  // if it's in provided diphthongs, use it
  const quick = diphthongs ? diphthongs[ipa] : null;
  if (quick && quick.length === 4) {
    return [{ f1: quick[0], f2: quick[1] }, { f1: quick[2], f2: quick[3] }]
  }

  const lookup = formants ?? allFormants;
  // segment the phonemes to deal with diacritics
  const segments = segmentPhonemes(ipa);
  const all: (FormantInfo | null)[] = segments.phonemes.map(p => {
    const use = approx(p, formants);
    const formant = lookup[ipa] ?? lookup[use];
    return formant ? { f1: formant[0], f2: formant[1] } : null;
  });
  const results = all.filter(f => f !== null) as FormantInfo[];
  return results;
}

/** find the closest match for a phoneme such as approximants */
function approx(p: PhonemeItem, formants?: Record<IpaType, number[]>): IpaType {
  const first = p.phoneme[0];
  switch (first) {
    case 'j': return 'i';
    case 'w': return 'u';
    case 'ɥ': return 'y';
    case 'ɰ': return 'ɯ';
    case 'ʕ': return 'ɑ';
    case 'ɹ':
    case 'r':
    case 'ɻ': return 'ɝ';
    case 'ɚ': return 'ə';
  }
  return getBestFormantMatch(p.phoneme);
}

/** get the most specific phoneme from the formants list, e.g., /o̝/ rather than /o/ */
export function getBestFormantMatch(ipa: IpaType, formants?: Record<IpaType, number[]>): IpaType {
  if (cacheAllSingles === null) {
    cacheAllSingles = Object.getOwnPropertyNames(allFormants);
  }

  // use either passed in formants or allFormants
  let list: string[] | null = null;
  if (formants) {
    list = Object.getOwnPropertyNames(formants);
  } else {
    list = cacheAllSingles;
  }

  // use if in list
  if (list.includes(ipa)) {
    return ipa;
  }

  // else look for most specific match
  for (let i = ipa.length - 1; i > 0; i--) {
    const sub = ipa.substring(0, i);
    if (list.includes(sub)) {
      return sub;
    }
  }

  return ipa;
}
let cacheAllSingles: string[] | null = null;


/**
 * vowels mapped to formant values F1,F2 based on the sounds chosen in ph_ipa
 * and recorded in speak/ipa.report,
 * generally trying to be close to recordings of the vowels done by others
 * https://www.internationalphoneticassociation.org/IPAcharts/inter_chart_2018/IPA_2018.html
 */
export const allFormants: Record<IpaType, number[]> = {
  i: [280, 2280], // i  i
  y: [280, 1980], // y  y_5
  ɨ: [336, 1760], // i^ i#_2
  ʉ: [304, 1560], // u^ u#_5
  ɯ: [336, 1440], // u- i#_7
  u̠: [256, 740],  // u# u_3
  u: [320, 860],  // u  u
  ɪ: [408, 1940], // I  ii_2
  ʏ: [392, 1700], // Y  u#
  ʊ: [388, 940],  // U  uu_2
  ʊ̞: [496, 1040], // U- 8_2
  e: [456, 1980], // e  e
  e̞: [504, 1900], // e- e_mid2
  ø: [448, 1520], // e. y#
  ø̞: [512, 1620], // e# @_fnt
  ɘ: [400, 1240], // e^ uu#
  ɵ: [352, 1000], // o# uu_3
  ɤ: [464, 1140], // U. 8_4
  ɤ̞: [520, 1160], // U# 8
  o: [454, 812],  // o  o
  o̞: [512, 860],  // o- oo_1
  o̝: [384, 736],  // o^ u_7
  ə: [632, 1300], // @  V_6
  ɛ: [646, 1780], // E  ee_5
  œ: [525, 1320], // E. oe
  ɜ: [525, 1441], // E^ @
  ɝ: [480, 1460], // P  vwl_en_us/3_us
  ɞ: [612, 1232], // O^ V
  ʌ: [640, 1140], // V  V_7
  ɔ: [552, 860],  // O  oo
  æ: [736, 1540], // &  &
  ɐ: [728, 1300], // &^ a#_2
  a: [904, 1320], // a  a
  ɶ: [800, 1320], // a. a#_4
  ɑ: [775, 1067], // A  aa_2
  ɒ: [552, 780],  // A. oo_3
  ɑ̝: [670, 946],  // A^ 0
  ä: [834, 1220], // a# a_5
  ɑ̈: [728, 1200], // A# aa_8
}

export const nasalFormants: Record<IpaType, number[]> = {
  ã: [920, 1300], // a~ _ipa/vnasal_a_n
  æ̃: [760, 1420], // &~ vnasal/&_n
  ɐ̃: [664, 1408], // &^~ vnasal/a#_n
  ẽ: [456, 1960], // e~ vnasal/e_n
  ɛ̃: [672, 1660], // E~ vnasal/ee_n
  ĩ: [312, 2240], // i~ vnasal/i_n2
  ɪ̃: [408, 1920], // I~ vnasal/ii_n
  õ: [448, 840],  // o~ vnasal/o_n
  ɔ̃: [540, 896],  // O~ vnasal/oo_n2
  ũ: [328, 900],  // u~ vnasal/u_n
  ʌ̃: [648, 1120], // V~ vnasal/aa_n
  ə̃: [612, 1232], // @~ vnasal/V_n
}

export const diphthongFormants: Record<IpaType, number[]> = {
  "ɛə̯": [700, 1680, 652, 1488], // E+@  vwl_en_us/a, en-us /æ/
  "ɯu̯": [352, 1380, 296, 1000], // u.   vdiph2/uw, en-us /u/
  "əʊ̯": [630, 1353, 390, 957],  // @+U  vdiph/@u_en
  "ɐʉ̯": [698, 1320, 280, 1600], // &+u  vdiph/au#
}

export const rhoticFormants: Record<IpaType, number[]> = {
  "ɝ": [480, 1460, 464, 1440],  // P    vwl_en_us/3_us
  "ɑɹ": [760, 1050, 520, 1260], // A+r  _ipa/A_r
  "e̞ɹ": [536, 1776, 520, 1400], // E+r  vwl_en_us/er
  "ɪɹ": [405, 1932, 506, 1460], // I+r  vwl_en_us/ir
  "oɹ": [468, 840, 520, 1160],  // o+r  vwl_en_us/or
  "ɤɹ": [456, 1080, 520, 1180], // U+r  vwl_en_us/ur
}

// used as the second half of a diphthong
export const nonsyllabicFormants: Record<IpaType, number[]> = {
  "a̯": [800, 1320, 904, 1320],  // a0   _ipa/short_a
  "ä̯": [760, 1230, 834, 1220],  // a#   _ipa/short_a_5
  "ɑ̯": [720, 1111, 775, 1067],  // A0   _ipa/short_aa_2
  "ɑ̝̯": [660, 1000, 690, 960],   // A^   _ipa/short_0
  "ɒ̯": [550, 820, 552, 780],    // A.   _ipa/short_oo_3
  "e̯": [456, 1880, 456, 1980],  // e0   _ipa/short_e
  "ø̯": [480, 1490, 448, 1520],  // e.0  _ipa/short_y#
  "ø̞̯": [512, 1570, 512, 1620],  // e#0  _ipa/short_@_fnt
  "ɘ̯": [430, 1250, 400, 1240],  // e^   _ipa/short_uu#
  "ɛ̯": [640, 1680, 646, 1780],  // E0   _ipa/short_ee_5
  "ɜ̯": [525, 1400, 525, 1441],  // E^0  _ipa/short_@
  "œ̯": [515, 1320, 525, 1320],  // E.   _ipa/short_oe
  "i̯": [355, 2222, 328, 2240],  // i0   j/j_
  "ɨ̯": [360, 1730, 336, 1760],  // i^0  _ipa/short_i#_2
  "ɪ̯": [425, 1870, 408, 1940],  // I0   _ipa/short_ii_2
  "o̯": [470, 860, 454, 812],    // o0   _ipa/short_o
  "ɔ̯": [552, 910, 552, 860],    // O0   _ipa/short_oo
  "u̯": [360, 910, 320, 860],    // u0   _ipa/short_u
  "ʉ̯": [354, 1520, 304, 1560],  // u^0  _ipa/short_u#_5
  "ʊ̯": [410, 970, 388, 940],    // U0   _ipa/short_uu_2
  "ʊ̞̯": [496, 1080, 496, 1040],  // U-   _ipa/short_8_2
  "ɤ̯": [490, 1150, 464, 1140],  // U.   _ipa/short_8_4
  "ʌ̯": [630, 1150, 640, 1140],  // V    _ipa/short_V_7
  "ɯ̯": [381, 1405, 336, 1440],  // u-   _ipa/short_i#_7
  "y̯": [310, 1930, 280, 1960],  // y0   _ipa/short_y_5
  "ɝ̯": [480, 1460, 464, 1440],  // P0   vwl_en_us/3_us
  "ə̯": [580, 1300, 632, 1300],  // @0   _ipa/short_V_6
  "æ̯": [700, 1510, 736, 1540],  // &0   _ipa/short_&
  "ɐ̯": [680, 1300, 728, 1300],  // &^   _ipa/short_a#_2

  "æ̯̃": [700, 1380, 760, 1420],  // &~   _ipa/short_nas_&_n
  "ɐ̯̃": [640, 1300, 680, 1340],  // &^~  _ipa/short_nas_a#_n
  "ẽ̯": [456, 1890, 456, 1960],  // e~   _ipa/short_nas_e_n
  "ɛ̯̃": [662, 1640, 672, 1720],  // E~   _ipa/short_nas_ee_n
  "ĩ̯": [332, 2180, 312, 2240],  // i~   _ipa/short_nas_i_n2
  "ɪ̯̃": [420, 1860, 408, 1920],  // I~   _ipa/short_nas_ii_n
  "õ̯": [448, 880, 448, 840],    // o~   _ipa/short_nas_o_n
  "ɔ̯̃": [530, 900, 530, 860],    // O~   _ipa/short_nas_oo_n2
  "ũ̯": [350, 940, 328, 900],    // u~   _ipa/short_nas_u_n
  "ʌ̯̃": [630, 1150, 648, 1120],  // V~   _ipa/short_nas_aa_n
  "ə̯̃": [600, 1240, 612, 1232],  // @~   _ipa/short_nas_V_n
}

export const syllabicFormants: Record<IpaType, number[]> = {
  "l̩": [352, 1240, 352, 1200], // l-
  "ɫ̩": [485, 891, 450, 792],   // L 
  "m̩": [512, 1300, 472, 1100], // m-  or [512, 1300, 370, 1040]
  "n̩": [496, 1512, 480, 1500], // n-  or [496, 1512, 380, 1780]
  "ŋ̩": [504, 1824, 490, 2100], // N-
  "r̩": [648, 1200, 640, 1200], // r-
  "ɹ̩": [480, 1460, 464, 1440], // P /ɝ/ vwl_en_us/3_us
}

/*
  Average vowel formants for a male voice according to https://en.wikipedia.org/wiki/Formant,
  often substantially different than the phonemes I've used
export const maleFormants: Record<IpaType, number[]> = {
  i: [240, 2400],
  y: [235, 2100],
  e: [390, 2300],
  ø: [370, 1900],
  ɛ: [610, 1900],
  œ: [585, 1710],
  a: [850, 1610],
  ɶ: [820, 1530],
  ɑ: [750, 940],
  ɒ: [700, 760],
  ʌ: [600, 1170],
  ɔ: [500, 700],
  ɤ: [460, 1310],
  o: [360, 640],
  ɯ: [300, 1390],
  u: [250, 595],
}
*/
